import { useCallback, useEffect, useRef, useState } from 'react';

/** How long the workspace may sit untouched before the session ends. */
export const IDLE_LIMIT_MS = 30 * 60 * 1000;

/** How long the warning stands before the session actually ends. */
export const IDLE_WARNING_MS = 60 * 1000;

/** Shared across tabs: activity in one keeps the others alive. */
const LAST_ACTIVITY = 'st.lastActivity';

/**
 * Writing on every mousemove would hit storage hundreds of times a minute for no gain. Once every
 * few seconds is far finer than a thirty-minute limit needs.
 */
const WRITE_EVERY_MS = 5000;

const EVENTS: (keyof WindowEventMap)[] = [
  'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll', 'mousemove',
];

function now() { return Date.now(); }

function readLastActivity(): number {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : now();
  } catch {
    return now();
  }
}

function writeLastActivity(at: number) {
  try { localStorage.setItem(LAST_ACTIVITY, String(at)); } catch { /* private mode */ }
}

/**
 * Ends the session after a spell of no activity.
 *
 * <p>
 * An ERP screen sits open on somebody's desk showing a customer's passport, a payment, a ledger. The
 * risk this closes is the ordinary one — a laptop left unlocked, a shared machine in an office —
 * so what counts as "activity" is what a person at the keyboard does, not what the app does. A page
 * polling in the background must not keep a session alive that nobody is sitting at.
 * </p>
 * <p>
 * The clock is shared between tabs through <code>localStorage</code>: somebody working in one tab
 * keeps every other tab of the same workspace alive, and a single stale tab cannot sign the whole
 * browser out from under them.
 * </p>
 * <p>
 * This is a CLIENT-side protection. It clears the tokens from this browser; it does not tell the
 * server that a token has stopped being trusted. Enforcing that would need the session's last
 * activity recorded server-side, which is a separate decision about absolute session lifetime.
 * </p>
 */
export function useIdleTimeout({
  enabled, onTimeout, limitMs = IDLE_LIMIT_MS, warningMs = IDLE_WARNING_MS,
}: {
  enabled: boolean;
  onTimeout: () => void;
  limitMs?: number;
  warningMs?: number;
}) {
  const [warningAt, setWarningAt] = useState<number | null>(null);
  const lastWrite = useRef(0);

  // Held in a ref so the ticking effect never has to be torn down and rebuilt when the parent
  // re-renders with a new closure -- restarting the interval would keep pushing the deadline out.
  const timeoutRef = useRef(onTimeout);
  timeoutRef.current = onTimeout;

  /** Called by a real interaction, and by "Stay signed in". */
  const markActive = useCallback(() => {
    const at = now();
    setWarningAt(null);

    if (at - lastWrite.current < WRITE_EVERY_MS) return;
    lastWrite.current = at;
    writeLastActivity(at);
  }, []);

  useEffect(() => {
    if (!enabled) { setWarningAt(null); return; }

    // A fresh sign-in starts the clock now rather than inheriting whatever the last session left
    // behind, which could otherwise sign somebody straight back out.
    writeLastActivity(now());
    lastWrite.current = now();

    const onActivity = () => {
      const at = now();
      if (at - lastWrite.current < WRITE_EVERY_MS) return;
      lastWrite.current = at;
      writeLastActivity(at);
      setWarningAt((current) => (current === null ? null : null));
    };

    for (const event of EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    // A second is fine for a thirty-minute limit and keeps the countdown honest to the second.
    const tick = window.setInterval(() => {
      const idleFor = now() - readLastActivity();

      if (idleFor >= limitMs) {
        timeoutRef.current();
        return;
      }

      setWarningAt(idleFor >= limitMs - warningMs ? readLastActivity() + limitMs : null);
    }, 1000);

    return () => {
      for (const event of EVENTS) window.removeEventListener(event, onActivity);
      window.clearInterval(tick);
    };
  }, [enabled, limitMs, warningMs]);

  return {
    /** When the session will end, while the warning is showing. Null the rest of the time. */
    warningAt,
    /** Seconds left, for the countdown. */
    secondsLeft: warningAt === null ? 0 : Math.max(0, Math.ceil((warningAt - now()) / 1000)),
    markActive,
  };
}
